/**
 * Media fact → display string helpers, shared by the detail page's tech
 * rows and the dashboard's session cards. Pure — no imports — so every
 * consumer stays node-testable.
 */

/** "hevc" → "HEVC", "h264" → "H.264"; unknown codecs upper-case. */
export function codecLabel(codec: string | null | undefined): string | null {
  if (!codec) return null;
  const known: Record<string, string> = {
    h264: "H.264",
    hevc: "HEVC",
    av1: "AV1",
    vp9: "VP9",
    aac: "AAC",
    ac3: "AC3",
    eac3: "EAC3",
    dts: "DTS",
    truehd: "TrueHD",
    opus: "Opus",
    flac: "FLAC",
    mp3: "MP3",
  };
  return known[codec.toLowerCase()] ?? codec.toUpperCase();
}

/** 6 → "5.1", 2 → "Stereo". */
export function channelsLabel(channels: number | undefined): string | null {
  if (!channels || channels <= 0) return null;
  if (channels === 1) return "Mono";
  if (channels === 2) return "Stereo";
  if (channels === 6) return "5.1";
  if (channels === 8) return "7.1";
  return `${channels} ch`;
}

/** BCP-47 → English display name; und/absent → null. */
export function languageName(code: string | null | undefined): string | null {
  if (!code || code === "und") return null;
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Jellyfin's resolution ladder (MediaStream.GetResolutionText): the first
 * 16:9 box a picture fits names it, so a 3840×1636 scope film is "4K" and
 * a 1920×800 one "1080p" — the height alone would call them 1636p and
 * 800p. Plex buckets the same way.
 */
const RESOLUTION_BOXES: ReadonlyArray<readonly [w: number, h: number, lines: number]> = [
  [256, 144, 144],
  [426, 240, 240],
  [640, 360, 360],
  [682, 384, 384],
  [720, 404, 404],
  [854, 480, 480],
  [960, 544, 540],
  [1024, 576, 576],
  [1280, 962, 720],
  [2560, 1440, 1080],
  [4096, 3072, 2160],
  [8192, 6144, 4320],
];

/**
 * The line count that names a picture: its ladder box when the width is
 * known, else the height itself. Null for an unknown height.
 */
export function resolutionLines(
  height: number | null | undefined,
  width?: number | null,
): number | null {
  if (!height || height <= 0) return null;
  if (!width || width <= 0) return height;
  const box = RESOLUTION_BOXES.find(([w, h]) => width <= w && height <= h);
  return box ? box[2] : height;
}

/** 3840×2160 (or ×1636) → "4K", 1920×1080 (or ×800) → "1080p", 7680×4320 → "8K". */
export function resolutionLabel(height: number | null | undefined, width?: number | null): string | null {
  const lines = resolutionLines(height, width);
  if (lines == null) return null;
  if (lines >= 4320) return "8K";
  return lines >= 2000 ? "4K" : `${lines}p`;
}

/**
 * Kilobits/sec → "10.8 Mbps" (integer when whole). The quality-ladder
 * label formatter; distinct name and rounding from the bytes/sec one
 * below — the two units are too easy to swap under one name.
 */
export function formatMbpsFromKbps(kbps: number): string {
  const mbps = kbps / 1000;
  const s = Number.isInteger(mbps) ? mbps.toFixed(0) : mbps.toFixed(1);
  return `${s} Mbps`;
}

/** kbps → "160 kbps" under a megabit, else the Mbps label above. */
export function formatKbps(kbps: number): string {
  return kbps < 1000 ? `${Math.round(kbps)} kbps` : formatMbpsFromKbps(kbps);
}

/**
 * Bytes/sec (the bandwidth chart's unit) → adaptive bitrate: "800 bps",
 * "989 Kbps", "9.2 Mbps", "1.2 Gbps". Zero reads "0 Kbps" — an idle
 * chart full of "0 bps" looks like a bug, not silence.
 */
export function formatBitrateFromBytes(bytesPerSec: number): string {
  const bits = bytesPerSec * 8;
  if (bits === 0) return "0 Kbps";
  let unit: string;
  let div: number;
  if (bits < 1e3) {
    unit = "bps";
    div = 1;
  } else if (bits < 1e6) {
    unit = "Kbps";
    div = 1e3;
  } else if (bits < 1e9) {
    unit = "Mbps";
    div = 1e6;
  } else {
    unit = "Gbps";
    div = 1e9;
  }
  const v = bits / div;
  const s = v >= 10 || Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
  return `${s} ${unit}`;
}

/** 4_500_000_000 → "4.5 GB"; sub-GB sizes render in MB. */
export function formatBytes(bytes: number | null | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  const gb = bytes / 1e9;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${Math.round(bytes / 1e6)} MB`;
}

/** "/v1/parts/42/file?token=x" → "42"; non-part paths → null. */
export function partIdFromPath(path: string): string | null {
  const m = /\/v1\/parts\/([^/?]+)\/file/.exec(path);
  return m?.[1] ?? null;
}
